import {
  OktonInput,
  OktonCheckbox,
  OktonAlert,
  OktonSearchSelect,
  OktonPageHeader,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  usaControlaDRE,
  useMoedas,
  useParametros,
  usePlanosDFC,
  usePlanosDRE,
  useSaveMoeda,
} from '../../../services/hooks/useCadastros';
import { useBancos } from '../../../services/hooks/useContasBancarias';

const VAZIO = {
  nome: '',
  banco: '',
  planocontas: '',
  planocontasdfcinicial: '',
  planocontasdfcfinal: '',
  principal: false,
  visivel: true,
};

/**
 * Cadastro de uma Moeda do tipo Banco.
 *
 * A tela não pergunta o Tipo de Título: a Moeda do próprio banco é a única que
 * se vincula a uma Conta e acumula saldo que atravessa o período, e é só dela
 * que o OktonBank trata. Os instrumentos que liquidam na Conta — PIX, Cartão —
 * e as Moedas de Caixa continuam sendo cadastrados pelo IFLEX, que pede os
 * campos próprios de cada um.
 */
const MoedaForm = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const { idMoeda } = useParams<{ idMoeda?: string }>();
  const edicao = !!idMoeda;

  const { data: moedas = [] } = useMoedas();
  const registro = moedas.find(m => m.id_moe === idMoeda);
  const { data: bancos = [] } = useBancos();
  // O Plano já gravado vai junto: se for um nível de classificação, de um
  // cadastro anterior à regra, ele continua aparecendo em vez de sumir.
  const { data: planosDRE = [] } = usePlanosDRE(registro?.planocontas_moe);
  // Duas listas de DFC porque o `atual` de cada campo é diferente: é ele que
  // segura na lista um Plano gravado antes da regra de só folhas.
  const { data: planosDFCInicial = [] } = usePlanosDFC(
    registro?.planocontasdfcinicial_moe,
  );
  const { data: planosDFCFinal = [] } = usePlanosDFC(
    registro?.planocontasdfcfinal_moe,
  );
  const { data: parametros } = useParametros();
  const controlaDRE = usaControlaDRE(parametros);
  const saveMutation = useSaveMoeda();

  const [form, setForm] = useState({ ...VAZIO });
  const [erro, setErro] = useState('');

  // A lista chega depois do primeiro render: sem isso a edição abriria vazia.
  useEffect(() => {
    if (registro) {
      setForm({
        nome: registro.nome_moe,
        banco: registro.banco_moe ?? '',
        planocontas: registro.planocontas_moe ?? '',
        planocontasdfcinicial: registro.planocontasdfcinicial_moe ?? '',
        planocontasdfcfinal: registro.planocontasdfcfinal_moe ?? '',
        principal: registro.principal_moe === '1',
        visivel: registro.visivel_moe === '1',
      });
    }
  }, [registro]);

  const salvar = async () => {
    if (!form.nome.trim()) {
      setErro('Informe o nome da Moeda.');
      return;
    }
    if (!form.banco) {
      setErro('Informe o Banco da Moeda.');
      return;
    }
    if (controlaDRE && !form.planocontas) {
      setErro('Informe o Plano de Contas (DRE).');
      return;
    }
    if (controlaDRE && !form.planocontasdfcinicial) {
      setErro('Informe o Plano de Contas Inicial (DFC).');
      return;
    }
    if (controlaDRE && !form.planocontasdfcfinal) {
      setErro('Informe o Plano de Contas Final (DFC).');
      return;
    }
    setErro('');
    try {
      await saveMutation.mutateAsync({
        id: idMoeda,
        nome: form.nome.trim(),
        banco: form.banco,
        planocontas: form.planocontas,
        planocontasdfcinicial: form.planocontasdfcinicial,
        planocontasdfcfinal: form.planocontasdfcfinal,
        principal: form.principal,
        visivel: form.visivel,
      });
      history.push('/moedas');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Erro ao gravar a Moeda.');
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title={edicao ? 'Editar Moeda' : 'Nova Moeda'}
        subtitle="Moeda do banco que liquida numa Conta Bancária"
        onBack={() => history.push('/moedas')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        <Stack spacing="4" maxW="720px">
          {!!erro && <OktonAlert status="error" description={erro} />}

          <Box>
            <Text
              as="label"
              htmlFor="moeda-nome"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Nome *
            </Text>
            <OktonInput
              name="moeda-nome"
              size="lg"
              rounded="2xl"
              fontWeight="light"
              color={color}
              maxLength={40}
              placeholder="Ex.: CONTA SICOOB"
              value={form.nome}
              onChange={e => {
                const valor = e.target.value;
                setForm(f => ({ ...f, nome: valor }));
              }}
            />
          </Box>

          <Box>
            <Text
              as="label"
              htmlFor="banco"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Banco *
            </Text>
            <OktonSearchSelect
              name="banco"
              placeholder="Selecione o Banco"
              value={form.banco}
              onChange={valor => setForm(f => ({ ...f, banco: valor }))}
              options={[
                ...bancos.map(b => ({
                  value: b.id_ban,
                  label: b.nome_ban,
                })),
              ]}
            />
            <Text fontSize="xs" color="gray.500" mt="1">
              Só pode ser vinculada a uma Conta do mesmo Banco.
            </Text>
          </Box>

          <Box>
            <Text
              as="label"
              htmlFor="planocontas"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Plano de Contas (DRE) {controlaDRE && '*'}
            </Text>
            <OktonSearchSelect
              name="planocontas"
              placeholder="Sem classificação"
              value={form.planocontas}
              onChange={valor => setForm(f => ({ ...f, planocontas: valor }))}
              options={[
                ...planosDRE.map(p => ({
                  value: p.id_pct,
                  label: `${p.item_pct} ${p.nome_pct}`,
                })),
              ]}
            />
          </Box>

          <Box>
            <Text
              as="label"
              htmlFor="planocontasdfcinicial"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Plano de Contas Inicial (DFC) {controlaDRE && '*'}
            </Text>
            <OktonSearchSelect
              name="planocontasdfcinicial"
              placeholder="Sem classificação"
              value={form.planocontasdfcinicial}
              onChange={valor =>
                setForm(f => ({
                  ...f,
                  planocontasdfcinicial: valor,
                }))
              }
              options={[
                ...planosDFCInicial.map(p => ({
                  value: p.id_pct,
                  label: `${p.item_pct} ${p.nome_pct}`,
                })),
              ]}
            />
            <Text fontSize="xs" color="gray.500" mt="1">
              Conta que recebe o saldo no início do período no Fluxo de Caixa.
            </Text>
          </Box>

          <Box>
            <Text
              as="label"
              htmlFor="planocontasdfcfinal"
              display="block"
              fontSize="sm"
              fontWeight="medium"
              mb="1"
            >
              Plano de Contas Final (DFC) {controlaDRE && '*'}
            </Text>
            <OktonSearchSelect
              name="planocontasdfcfinal"
              placeholder="Sem classificação"
              value={form.planocontasdfcfinal}
              onChange={valor =>
                setForm(f => ({
                  ...f,
                  planocontasdfcfinal: valor,
                }))
              }
              options={[
                ...planosDFCFinal.map(p => ({
                  value: p.id_pct,
                  label: `${p.item_pct} ${p.nome_pct}`,
                })),
              ]}
            />
            <Text fontSize="xs" color="gray.500" mt="1">
              Conta que recebe o saldo no final do período no Fluxo de Caixa.
            </Text>
          </Box>

          <Stack spacing="2">
            <OktonCheckbox
              name="moeda-visivel"
              size="md"
              label={<Text fontSize="sm">Visível no Faturamento</Text>}
              isChecked={form.visivel}
              onChange={e => {
                const marcado = e.target.checked;
                setForm(f => ({ ...f, visivel: marcado }));
              }}
            />
            <OktonCheckbox
              name="moeda-principal"
              size="md"
              label={<Text fontSize="sm">Moeda Principal</Text>}
              isChecked={form.principal}
              onChange={e => {
                const marcado = e.target.checked;
                setForm(f => ({ ...f, principal: marcado }));
              }}
            />
          </Stack>

          <Text fontSize="xs" color="gray.500">
            Esta tela cadastra só a Moeda do próprio banco, que é a que se
            vincula a uma Conta pela tela de Contas Bancárias. PIX, Cartão e as
            demais formas de pagamento continuam no cadastro de Moeda do IFLEX.
            Só pode haver uma Moeda Principal na base.
          </Text>

          <Flex gridGap="3" pt="2">
            <Button variant="ghost" onClick={() => history.push('/moedas')}>
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={
                saveMutation.isLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <Check size={16} />
                )
              }
              isDisabled={saveMutation.isLoading}
              onClick={salvar}
            >
              Salvar
            </Button>
          </Flex>
        </Stack>
      </Box>
    </BankAppPage>
  );
};

export default MoedaForm;
